import { type ChangeEvent, useState } from 'react'
import {
  type MetaFunction,
  Form,
  Link,
  useLoaderData,
  type ActionFunctionArgs,
  redirect,
  data,
} from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Field } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { createEnrollmentForUser, createUser } from '#app/controls/users.ts'
import { type AccessCode } from '#app/types/access-code.js'
import { type OrganizationWithAccessCodes } from '#app/types/organization.ts'
import { getOrganizationList } from '#app/utils/admin-api.server.ts'
import { executeQuery } from '#app/utils/cms.server.ts'
import { requireControlsToBeEnabled } from '#app/utils/controls.server.ts'
import { siteTitle } from '#app/utils/global.ts'
import { type LocalizedContent } from '#app/utils/i18n.ts'
import { invariantResponse } from '#app/utils/misc.tsx'
import { Status } from '#app/utils/status.js'

interface TrainingProgram {
  _id: string
  title: LocalizedContent
}

export async function loader() {
  requireControlsToBeEnabled()

  const trainingProgramQuery = `*[_type == 'trainingProgram']{_id, title}`
  const trainingProgramResult = await executeQuery(trainingProgramQuery)

  if (trainingProgramResult.status !== Status.Success) {
    throw data(
      {
        title: 'Error',
        description: 'There was an error fetching the training programs',
      },
      { status: 500 },
    )
  }

  const trainingPrograms = trainingProgramResult.data as TrainingProgram[]

  const trainingProgramsById: Record<string, TrainingProgram> = {}
  trainingPrograms.forEach(
    trainingProgram =>
      (trainingProgramsById[trainingProgram._id] = trainingProgram),
  )

  const organizationListResult = await getOrganizationList()
  if (organizationListResult.status !== Status.Success) {
    throw data(
      {
        title: 'Error',
        description: 'There was an error fetching the organizations',
      },
      { status: 500 },
    )
  }

  const organizations =
    organizationListResult.data as OrganizationWithAccessCodes[]

  const organizationsWithAccessCodes = organizations.filter(
    organization => organization.accessCodes.length > 0,
  )

  if (organizationsWithAccessCodes.length === 0) {
    throw data(
      {
        title: 'Error',
        description: 'There are no organizations with training programs',
      },
      { status: 500 },
    )
  }

  const defaultOrganization = organizationsWithAccessCodes[0]!
  const defaultOrganizationId = defaultOrganization.id

  const organizationsById: Record<string, OrganizationWithAccessCodes> = {}
  organizationsWithAccessCodes.forEach(
    organization => (organizationsById[organization.id] = organization),
  )

  return {
    trainingPrograms: trainingProgramsById,
    organizations: organizationsById,
    defaultOrganizationId,
  }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()

  const email = formData.get('email')
  invariantResponse(typeof email === 'string', 'Email must be a string')

  const firstName = formData.get('firstName')
  invariantResponse(
    typeof firstName === 'string',
    'First name must be a string',
  )

  const lastName = formData.get('lastName')
  invariantResponse(typeof lastName === 'string', 'Last name must be a string')

  const password = formData.get('password')
  invariantResponse(typeof password === 'string', 'Password must be a string')

  const locale = formData.get('locale')
  invariantResponse(typeof locale === 'string', 'Locale must be a string')

  const organizationId = formData.get('organizationId')
  invariantResponse(
    typeof organizationId === 'string',
    'Organization ID must be a string',
  )

  const accessCodeId = formData.get('accessCodeId')
  invariantResponse(
    typeof accessCodeId === 'string',
    'Access code ID must be a string',
  )

  const trainingProgramId = formData.get('trainingProgramId')
  invariantResponse(
    typeof trainingProgramId === 'string',
    'Training program ID must be a string',
  )

  const user = await createUser({
    email,
    firstName,
    lastName,
    password,
    locale,
    organizationId,
    accessCodeId,
  })

  await createEnrollmentForUser(user.id, trainingProgramId)

  return redirect('/controls/users')
}

export default function CreateUserControlRoute() {
  const { trainingPrograms, organizations, defaultOrganizationId } =
    useLoaderData<typeof loader>()

  const defaultOrganization = organizations[defaultOrganizationId]!

  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(
    defaultOrganizationId,
  )

  const [organizationAccessCodes, setOrganizationAccessCodes] = useState<
    AccessCode[]
  >(defaultOrganization.accessCodes)

  const [accessCodeId, setAccessCodeId] = useState<string | undefined>()

  const onChangeOrganizationId = (event: ChangeEvent<HTMLSelectElement>) => {
    const organizationId = event.target.value
    setSelectedOrganizationId(organizationId)
    const organization = organizations[organizationId]
    if (organization) {
      setOrganizationAccessCodes(organization.accessCodes)
    }
    setAccessCodeId(undefined)
  }

  const onChangeTrainingProgramId = (event: ChangeEvent<HTMLInputElement>) => {
    const trainingProgramId = event.target.value
    const accessCode = organizationAccessCodes.find(
      accessCode => accessCode.trainingProgramId === trainingProgramId,
    )
    setAccessCodeId(accessCode?.id)
  }

  return (
    <>
      <ul className="flex gap-3">
        <li>
          <Link to={'/controls'}>Controls</Link>
        </li>
        <li className="flex items-center gap-3">
          ▶️
          <span className="inline-flex items-center gap-1.5">
            <Link to={'/controls/users'}>Users</Link>
          </span>
        </li>
        <li className="flex items-center gap-3">
          ▶️
          <span className="inline-flex items-center gap-1.5">
            Create specific user
          </span>
        </li>
      </ul>
      <Form className="py-6" method="POST">
        <Field
          labelProps={{ children: 'Email' }}
          inputProps={{ name: 'email', type: 'email' }}
        />
        <Field
          labelProps={{ children: 'First Name' }}
          inputProps={{ name: 'firstName' }}
        />
        <Field
          labelProps={{ children: 'Last Name' }}
          inputProps={{ name: 'lastName' }}
        />
        <Field
          labelProps={{ children: 'Password' }}
          inputProps={{ name: 'password', type: 'password' }}
        />

        <fieldset className="mb-6">
          <legend>Select a locale:</legend>
          <div>
            <input
              className="mr-2"
              type="radio"
              id="en"
              name="locale"
              value="en"
            />
            <label htmlFor="en">English</label>
          </div>

          <div>
            <input
              className="mr-2"
              type="radio"
              id="es"
              name="locale"
              value="es"
            />
            <label htmlFor="es">Español</label>
          </div>
        </fieldset>

        <div className="mb-6">
          <div>
            <label htmlFor="organization-select">Organization</label>
          </div>
          <select
            name="organizationId"
            id="organization-select"
            value={selectedOrganizationId}
            onChange={onChangeOrganizationId}
          >
            {Object.values(organizations).map(organization => (
              <option key={organization.id} value={organization.id}>
                {organization.name}
              </option>
            ))}
          </select>
        </div>

        <fieldset className="mb-6">
          <legend>Select a training program:</legend>
          {organizationAccessCodes.map(accessCode => {
            const trainingProgram =
              trainingPrograms[accessCode.trainingProgramId]

            if (!trainingProgram) return null

            return (
              <div key={trainingProgram._id}>
                <input
                  className="mr-2"
                  type="radio"
                  id={trainingProgram._id}
                  name="trainingProgramId"
                  value={trainingProgram._id}
                  onChange={onChangeTrainingProgramId}
                />
                <label htmlFor={trainingProgram._id}>
                  {trainingProgram.title.en}
                </label>
              </div>
            )
          })}
        </fieldset>

        <input type="hidden" name="accessCodeId" value={accessCodeId} />

        <Button type="submit">Submit</Button>
      </Form>
    </>
  )
}

export const meta: MetaFunction = () => {
  return [{ title: `Controls > Create specific user | ${siteTitle}` }]
}

export function ErrorBoundary() {
  return (
    <GeneralErrorBoundary
      statusHandlers={{
        500: ({ error }) => (
          <div className="text-center">
            <h1>{error.data.title}</h1>
            <p className="text-3 mt-2 font-normal">{error.data.description}</p>
          </div>
        ),
      }}
    />
  )
}
